import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import ResponsiveTooltip from '../../ResponsiveTooltip';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { useAuth } from '../../../context/AuthContext';

interface TaxSectionProps {
    onLearnMore?: () => void;
}

const TaxSection: React.FC<TaxSectionProps> = ({ onLearnMore }) => {
    const { t } = useTranslation();
    const { currentCtUser } = useAuth();
    
    return (
        <Paper 
            variant="outlined" 
            sx={{ 
                p: 3, 
                bgcolor: 'background.paper',
                height: 'auto',
                display: 'flex',
                flexDirection: 'column'
            }}
        >
            <Typography 
                variant="h6" 
                gutterBottom 
                sx={{ 
                    color: 'text.primary', 
                    fontWeight: 'bold',
                    fontSize: '1.25rem',
                    mb: 2
                }}
            >
                {t('settings.tax.title')}
            </Typography>
            
            <Stack spacing={2}>
                <Box sx={{ display: 'flex' }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="bold" sx={{ mr: 1, fontSize: '1rem' }}>
                        {t('settings.tax.country')}:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                        {t('settings.tax.values.germany')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex' }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="bold" sx={{ mr: 1, fontSize: '1rem' }}>
                        {t('settings.tax.localCurrency')}:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                        {currentCtUser?.currency || 'EUR'}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="bold" sx={{ mr: 1, fontSize: '1rem' }}>
                        {t('settings.tax.inventoryMethod')}:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                        {t('settings.tax.values.fifo')}
                    </Typography>
                    <ResponsiveTooltip 
                        title={
                            <Box sx={{ p: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                    {t('settings.tax.tooltips.inventoryMethod')}
                                </Typography>
                            </Box>
                        }
                        arrow
                        placement="right"
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    p: 0,
                                    bgcolor: (theme) => theme.palette.background.paper,
                                    color: (theme) => theme.palette.text.primary,
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                    border: '1px solid',
                                    borderColor: (theme) => theme.palette.divider,
                                    borderRadius: 1,
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold',
                                    '& .MuiTooltip-arrow': {
                                        color: (theme) => theme.palette.background.paper,
                                    }
                                },
                            },
                            popper: {
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        p: 0,
                                    }
                                }
                            }
                        }}
                    >
                        <InfoOutlinedIcon 
                            sx={{
                                ml: 0.5,
                                fontSize: '1.1rem',
                                color: 'text.secondary',
                                cursor: 'help',
                            }} 
                        />
                    </ResponsiveTooltip>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary" fontWeight="bold" sx={{ mr: 1, fontSize: '1rem' }}>
                        {t('settings.tax.costBasis')}:
                    </Typography>
                    <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '1rem' }}>
                        {t('settings.tax.values.perAccount')}
                    </Typography>
                    <ResponsiveTooltip 
                        title={
                            <Box sx={{ p: 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                                    {t('settings.tax.tooltips.costBasis')}
                                </Typography>
                            </Box>
                        }
                        arrow
                        placement="right"
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    p: 0,
                                    bgcolor: (theme) => theme.palette.background.paper,
                                    color: (theme) => theme.palette.text.primary,
                                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                    border: '1px solid',
                                    borderColor: (theme) => theme.palette.divider,
                                    borderRadius: 1,
                                    fontSize: '0.9rem',
                                    fontWeight: 'bold',
                                    '& .MuiTooltip-arrow': {
                                        color: (theme) => theme.palette.background.paper,
                                    }
                                },
                            },
                            popper: {
                                sx: {
                                    '& .MuiTooltip-tooltip': {
                                        p: 0,
                                    }
                                }
                            }
                        }}
                    >
                        <InfoOutlinedIcon 
                            sx={{
                                ml: 0.5,
                                fontSize: '1.1rem',
                                color: 'text.secondary',
                                cursor: 'help',
                            }} 
                        />
                    </ResponsiveTooltip>
                </Box>

                {onLearnMore && (
                    <Button
                        variant="text"
                        onClick={onLearnMore}
                        startIcon={<OpenInNewIcon />}
                        sx={{ 
                            color: 'primary.main',
                            textTransform: 'none',
                            justifyContent: 'flex-start',
                            p: 0,
                            fontSize: '1rem'
                        }}
                    >
                        {t('settings.tax.buttons.learnMore')}
                    </Button>
                )}
            </Stack>
        </Paper>
    );
};

export default TaxSection;
